import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { AVAILABLE_FONTS, getFontFamilyCss } from '../../utils/fontHelper';

export const resolvePilarFontSizeCss = (val, defaultVal = '0.94rem') => {
  if (!val) return defaultVal;
  if (val === '2xs' || val === 'xxs') return '0.68rem';
  if (val === 'xs' || val === 'xsmall') return '0.75rem';
  if (val === 'sm' || val === 'small') return '0.84rem';
  if (val === 'md' || val === 'medium') return '0.95rem';
  if (val === 'lg' || val === 'large') return '1.10rem';
  if (val === 'xl' || val === 'xlarge') return '1.25rem';
  if (val === 'xxl' || val === '2xl') return '1.45rem';
  return val;
};

export const resolvePilarFontWeightCss = (val, defaultVal = 600) => {
  const map = { normal: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800, black: 900 };
  return map[val] || val || defaultVal;
};

export const normalizeBenefitsList = (rawList, defaultList) => {
  if (!Array.isArray(rawList) || rawList.length === 0) {
    return defaultList;
  }
  return rawList.map((item, idx) => {
    const defItem = defaultList.find(d => d.id === item.id) || defaultList[idx] || {};
    const hasExplicitLine3 = item.extraText !== undefined || item.line3 !== undefined || item.subtitle !== undefined;
    return {
      ...defItem,
      ...item,
      extraText: hasExplicitLine3
        ? (item.extraText ?? item.line3 ?? item.subtitle ?? '')
        : (defItem.extraText || '')
    };
  });
};

export const DEFAULT_BENEFITS_PRIMARY = [
  {
    id: 'b-1',
    icon: 'bi-truck',
    title: 'Envíos a Todo el País',
    description: 'Logística rápida y segura a todas las provincias',
    extraText: 'Despacho rápido garantizado',
    link: ''
  },
  {
    id: 'b-2',
    icon: 'bi-boxes',
    title: 'Fabricantes Directos',
    description: 'Precios de fábrica en cartón y embalajes',
    extraText: 'Venta directa sin intermediarios',
    link: ''
  },
  {
    id: 'b-3',
    icon: 'bi-tags-fill',
    title: 'Mayorista y Minorista',
    description: 'Escalas por cantidad y listas especiales',
    extraText: 'Precios especiales por volumen',
    link: ''
  },
  {
    id: 'b-4',
    icon: 'bi-whatsapp',
    title: 'Atención Personalizada',
    description: 'Asesoramiento ágil directo por WhatsApp',
    extraText: 'Respuesta inmediata de asesores',
    link: ''
  }
];

export const DEFAULT_BENEFITS_SECONDARY = [
  {
    id: 'b2-1',
    icon: 'bi-clock-history',
    title: 'Despacho en 24/48hs',
    description: 'Preparación ágil de pedidos para entrega rápida',
    extraText: 'Envíos a todo el país',
    link: ''
  },
  {
    id: 'b2-2',
    icon: 'bi-credit-card',
    title: 'Múltiples Medios de Pago',
    description: 'Transferencias, tarjetas y facturación directa',
    extraText: 'Facturación directa A y B',
    link: ''
  },
  {
    id: 'b2-3',
    icon: 'bi-patch-check',
    title: 'Garantía de Calidad',
    description: 'Materiales testeados de máxima resistencia',
    extraText: 'Directo de nuestra fábrica',
    link: ''
  },
  {
    id: 'b2-4',
    icon: 'bi-chat-dots',
    title: 'Cotizaciones Especiales',
    description: 'Presupuestos personalizados por cantidad y volumen',
    extraText: 'Asesoramiento por WhatsApp',
    link: ''
  }
];

export const DEFAULT_BENEFITS_SETTINGS = {
  fontFamily: 'inherit',
  backgroundColor: '#f8fafc',
  cardBackgroundColor: '#ffffff',
  // Línea 1 (Título Principal)
  line1FontFamily: 'inherit',
  line1Color: '#1e293b',
  line1FontSize: 'md',
  line1FontWeight: 'bold',
  // Línea 2 (Bajada / Descripción)
  line2FontFamily: 'inherit',
  line2Color: '#64748b',
  line2FontSize: 'sm',
  line2FontWeight: 'normal',
  // Línea 3 (Línea Adicional / Detalle)
  line3FontFamily: 'inherit',
  line3Color: '#0d6efd',
  line3FontSize: 'xs',
  line3FontWeight: 'medium'
};

const POPULAR_BENEFIT_ICONS = [
  { icon: 'bi-truck', label: 'Envíos' },
  { icon: 'bi-boxes', label: 'Fábrica' },
  { icon: 'bi-tags-fill', label: 'Precios' },
  { icon: 'bi-whatsapp', label: 'WhatsApp' },
  { icon: 'bi-clock-history', label: '24/48hs' },
  { icon: 'bi-lightning-charge-fill', label: 'Rápido' },
  { icon: 'bi-credit-card', label: 'Pagos' },
  { icon: 'bi-shield-check', label: 'Garantía' },
  { icon: 'bi-star-fill', label: 'Calidad' },
  { icon: 'bi-award-fill', label: 'Líderes' },
  { icon: 'bi-building', label: 'Industria' },
  { icon: 'bi-geo-alt-fill', label: 'Ubicación' },
  { icon: 'bi-headset', label: 'Soporte' },
  { icon: 'bi-chat-dots-fill', label: 'Asesoría' },
  { icon: 'bi-cart-check-fill', label: 'Stock' },
  { icon: 'bi-arrow-repeat', label: 'Cambios' },
  { icon: 'bi-file-earmark-check', label: 'Factura A' },
  { icon: 'bi-patch-check-fill', label: 'Certificado' }
];

export const BenefitsAdminPanel = ({
  config,
  setConfig,
  onToggleHomeSection,
  currentHomeSections = [],
  initialBar = 'primary'
}) => {
  const [activeBar, setActiveBar] = useState(initialBar);

  useEffect(() => {
    if (initialBar) {
      setActiveBar(initialBar);
    }
  }, [initialBar]);

  const isPrimary = activeBar === 'primary';
  const configKey = isPrimary ? 'benefits' : 'benefitsSecondary';
  const sectionId = isPrimary ? 'benefits' : 'benefitsSecondary';
  const defaultList = isPrimary ? DEFAULT_BENEFITS_PRIMARY : DEFAULT_BENEFITS_SECONDARY;

  const settingsKey = isPrimary ? 'benefitsSettings' : 'benefitsSecondarySettings';
  const currentSecObj = currentHomeSections.find(s => s.id === sectionId) || {};
  const barSettings = {
    ...DEFAULT_BENEFITS_SETTINGS,
    ...(config[settingsKey] || {}),
    backgroundColor: (config[settingsKey]?.backgroundColor || currentSecObj.backgroundColor || DEFAULT_BENEFITS_SETTINGS.backgroundColor)
  };

  const handleUpdateSetting = (field, value) => {
    setConfig(prev => {
      const nextSettings = {
        ...DEFAULT_BENEFITS_SETTINGS,
        ...(prev[settingsKey] || {}),
        [field]: value
      };
      const nextHomeSections = field === 'backgroundColor' && Array.isArray(prev.homeSections)
        ? prev.homeSections.map(s => s.id === sectionId ? { ...s, backgroundColor: value } : s)
        : prev.homeSections;
      return {
        ...prev,
        [settingsKey]: nextSettings,
        ...(nextHomeSections ? { homeSections: nextHomeSections } : {})
      };
    });
  };

  const rawItems = config[configKey];
  const items = normalizeBenefitsList(rawItems, defaultList);

  const isSectionEnabled = currentHomeSections.find(s => s.id === sectionId)?.enabled ?? isPrimary;

  const handleUpdateItem = (idx, field, value) => {
    const updated = items.map((item, i) => i === idx ? { ...item, [field]: value } : item);
    setConfig(prev => ({
      ...prev,
      [configKey]: updated
    }));
  };

  const handleAddItem = () => {
    const newItem = {
      id: `${isPrimary ? 'b' : 'b2'}-${Date.now()}`,
      icon: 'bi-check-circle-fill',
      title: 'Nuevo Diferencial Comercial',
      description: 'Escribí aquí los detalles o beneficios de este servicio.',
      extraText: 'Detalle o texto adicional',
      link: ''
    };
    setConfig(prev => ({
      ...prev,
      [configKey]: [...items, newItem]
    }));
  };

  const handleDeleteItem = (idx) => {
    if (items.length <= 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Mínimo un pilar',
        text: 'Debe haber al menos un pilar o diferencial en la sección.',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }
    const updated = items.filter((_, i) => i !== idx);
    setConfig(prev => ({
      ...prev,
      [configKey]: updated
    }));
  };

  const handleMoveItem = (idx, direction) => {
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const updated = [...items];
    const temp = updated[idx];
    updated[idx] = updated[targetIdx];
    updated[targetIdx] = temp;
    setConfig(prev => ({
      ...prev,
      [configKey]: updated
    }));
  };

  const handleResetToDefaults = () => {
    Swal.fire({
      title: '¿Restablecer esta barra?',
      text: 'Se restablecerán los pilares a los textos e iconos predeterminados de fábrica.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, restablecer',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0d6efd'
    }).then(result => {
      if (result.isConfirmed) {
        setConfig(prev => ({
          ...prev,
          [configKey]: defaultList
        }));
        Swal.fire({
          toast: true,
          position: 'top',
          icon: 'success',
          title: 'Pilares restablecidos correctamente',
          showConfirmButton: false,
          timer: 1600
        });
      }
    });
  };

  return (
    <div className="bg-white p-4 rounded-4 border shadow-sm">
      {/* Encabezado General */}
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 pb-3 border-bottom gap-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="badge bg-primary-subtle text-primary fw-bold text-uppercase px-3 py-1 rounded-pill">
              Pilares Comerciales
            </span>
            <span className="badge bg-light text-muted border font-monospace">Portada Principal</span>
          </div>
          <h4 className="fw-black text-dark mb-1" style={{ fontWeight: 800 }}>
            Barra de Beneficios y Diferenciales (Pilares)
          </h4>
          <p className="text-muted small mb-0">
            Administrá el contenido, iconos, enlaces y la cantidad de recuadros que comunican las fortalezas de tu empresa.
          </p>
        </div>

        {/* Switch de activación de la sección activa */}
        <div className="d-flex align-items-center gap-2 bg-light px-3 py-2 rounded-pill border">
          <span className="small fw-bold text-dark">
            {isSectionEnabled ? 'Sección Visible' : 'Sección Oculta'}
          </span>
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input cursor-pointer"
              type="checkbox"
              role="switch"
              checked={Boolean(isSectionEnabled)}
              onChange={(e) => onToggleHomeSection && onToggleHomeSection(sectionId, e.target.checked)}
              style={{ transform: 'scale(1.15)' }}
            />
          </div>
        </div>
      </div>

      {/* Selector de Sub-Secciones (Barra 1 vs Barra 2) */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-4 p-1.5 bg-light rounded-4 border">
        <button
          type="button"
          className={`btn btn-sm rounded-pill px-4 py-2 fw-bold transition-all d-flex align-items-center gap-2 ${
            activeBar === 'primary' ? 'btn-white bg-white text-primary shadow-xs' : 'btn-link text-secondary text-decoration-none'
          }`}
          onClick={() => setActiveBar('primary')}
        >
          <i className="bi bi-shield-check"></i>
          <span>Barra Principal (Pilares 1)</span>
          <span className={`badge ${currentHomeSections.find(s => s.id === 'benefits')?.enabled !== false ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'} rounded-pill ms-1`}>
            {config.benefits?.length || 4} items
          </span>
        </button>

        <button
          type="button"
          className={`btn btn-sm rounded-pill px-4 py-2 fw-bold transition-all d-flex align-items-center gap-2 ${
            activeBar === 'secondary' ? 'btn-white bg-white text-primary shadow-xs' : 'btn-link text-secondary text-decoration-none'
          }`}
          onClick={() => setActiveBar('secondary')}
        >
          <i className="bi bi-shield-plus"></i>
          <span>Segunda Barra (Pilares 2)</span>
          <span className={`badge ${currentHomeSections.find(s => s.id === 'benefitsSecondary')?.enabled ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'} rounded-pill ms-1`}>
            {config.benefitsSecondary?.length || 4} items
          </span>
        </button>
      </div>

      {/* Sub-Header de la Barra Seleccionada con Acciones */}
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3 mb-4 p-3 rounded-4 bg-light bg-opacity-50 border">
        <div>
          <h6 className="fw-bold text-dark mb-0.5 d-flex align-items-center gap-2">
            <i className={`bi ${isPrimary ? 'bi-shield-check text-primary' : 'bi-shield-plus text-success'}`}></i>
            <span>{isPrimary ? 'Barra Principal de Pilares (Sección #1)' : 'Segunda Barra de Pilares (Sección #2)'}</span>
            <span className="badge bg-primary text-white rounded-pill px-2.5 py-0.5" style={{ fontSize: '0.72rem' }}>
              {items.length} {items.length === 1 ? 'Pilar' : 'Pilares'}
            </span>
          </h6>
          <p className="text-muted small mb-0" style={{ fontSize: '0.8rem' }}>
            {isPrimary
              ? 'Ubicada habitualmente justo debajo del Hero Slider o cabecera principal.'
              : 'Sección adicional solicitada para ubicar más abajo (por ej. antes del pie o entre secciones).'}
          </p>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm rounded-pill fw-semibold px-3 py-1.5"
            onClick={handleResetToDefaults}
            title="Restablecer textos e iconos sugeridos"
            style={{ fontSize: '0.8rem' }}
          >
            <i className="bi bi-arrow-counterclockwise me-1"></i> Restablecer
          </button>

          <button
            type="button"
            className="btn btn-primary btn-sm rounded-pill fw-bold px-3 py-1.5 shadow-xs d-flex align-items-center gap-1.5"
            onClick={handleAddItem}
            style={{ fontSize: '0.82rem' }}
          >
            <i className="bi bi-plus-circle-fill"></i>
            <span>Agregar Nuevo Pilar</span>
          </button>
        </div>
      </div>

      {/* Panel de Configuración de Tipografías y Colores (3 Líneas) */}
      <div className="card border rounded-4 mb-4 bg-light bg-opacity-50 overflow-hidden shadow-2xs">
        <div className="card-header bg-white py-2.5 px-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-circle bg-primary-subtle text-primary p-2 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
              <i className="bi bi-fonts fs-5"></i>
            </div>
            <div>
              <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '0.9rem' }}>
                Tipografía y Formato de las 3 Líneas ({isPrimary ? 'Barra Principal' : 'Segunda Barra'})
              </h6>
              <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                Cada línea (1, 2 y 3) cuenta con selector independiente de fuente (Font), tamaño, grosor y color.
              </small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="d-flex align-items-center gap-2 flex-nowrap bg-light px-3 py-2 rounded-3 border shadow-2xs">
              <div
                className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: '28px', height: '28px', fontSize: '0.88rem' }}
              >
                <i className="bi bi-palette-fill"></i>
              </div>
              <label className="small fw-bold text-dark mb-0 text-nowrap" style={{ fontSize: '0.82rem' }}>
                Fondo detrás de Pilares:
              </label>
              <input
                type="color"
                className="form-control form-control-color border rounded-3 p-1 cursor-pointer flex-shrink-0 bg-white shadow-2xs"
                style={{ width: '36px', height: '30px' }}
                value={barSettings.backgroundColor || '#f8fafc'}
                onChange={(e) => handleUpdateSetting('backgroundColor', e.target.value)}
                title="Color de fondo de la franja detrás de los pilares"
              />
              <input
                type="text"
                className="form-control form-control-sm font-monospace rounded-3 px-2"
                style={{ width: '92px', fontSize: '0.8rem', height: '30px' }}
                value={barSettings.backgroundColor || '#f8fafc'}
                onChange={(e) => handleUpdateSetting('backgroundColor', e.target.value)}
              />
            </div>

            <div className="d-flex align-items-center gap-2 flex-nowrap">
              <label className="small fw-bold text-dark mb-0 me-1 text-nowrap">
                <i className="bi bi-type me-1 text-primary"></i> Fuente Base de la Barra:
              </label>
              <select
                className="form-select form-select-sm fw-semibold rounded-3"
                style={{ minWidth: '220px' }}
                value={barSettings.fontFamily || 'inherit'}
                onChange={(e) => handleUpdateSetting('fontFamily', e.target.value)}
              >
                <option value="inherit">Heredar del sitio (Principal)</option>
                {AVAILABLE_FONTS.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="card-body p-3 p-md-4 bg-white">
          <div className="row g-3">
            {/* Columna Línea 1: Título Principal */}
            <div className="col-12 col-md-4">
              <div className="p-3 rounded-4 border bg-light bg-opacity-75 h-100">
                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-primary text-white rounded-pill px-2 py-1" style={{ fontSize: '0.7rem' }}>
                      Línea 1
                    </span>
                    <strong className="small text-dark">Título Principal</strong>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    <i className="bi bi-fonts me-1 text-primary"></i> Tipografía (Fuente Línea 1)
                  </label>
                  <select
                    className="form-select form-select-sm fw-semibold rounded-3"
                    value={barSettings.line1FontFamily || 'inherit'}
                    onChange={(e) => handleUpdateSetting('line1FontFamily', e.target.value)}
                  >
                    <option value="inherit">Heredar de la barra / sitio</option>
                    {AVAILABLE_FONTS.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    <i className="bi bi-arrows-angle-expand me-1 text-primary"></i> Tamaño de Fuente (Línea 1)
                  </label>
                  <select
                    className="form-select form-select-sm fw-semibold rounded-3"
                    value={barSettings.line1FontSize || 'md'}
                    onChange={(e) => handleUpdateSetting('line1FontSize', e.target.value)}
                  >
                    <option value="xs">Muy Chico (12px / 0.75rem)</option>
                    <option value="sm">Chico (13.5px / 0.84rem)</option>
                    <option value="md">Mediano (15px / 0.95rem)</option>
                    <option value="lg">Grande (17.5px / 1.10rem)</option>
                    <option value="xl">Extra Grande (20px / 1.25rem)</option>
                    <option value="xxl">Doble Extra Grande (23px / 1.45rem)</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    Grosor (Font Weight)
                  </label>
                  <select
                    className="form-select form-select-sm fw-semibold rounded-3"
                    value={barSettings.line1FontWeight || 'bold'}
                    onChange={(e) => handleUpdateSetting('line1FontWeight', e.target.value)}
                  >
                    <option value="normal">Normal (400)</option>
                    <option value="medium">Medio (500)</option>
                    <option value="semibold">Semi-Negrita (600)</option>
                    <option value="bold">Negrita (700)</option>
                    <option value="extrabold">Extra-Negrita (800)</option>
                  </select>
                </div>

                <div>
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    Color del Texto
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="color"
                      className="form-control form-control-color border rounded-3 p-1 cursor-pointer"
                      style={{ width: '36px', height: '30px' }}
                      value={barSettings.line1Color || '#1e293b'}
                      onChange={(e) => handleUpdateSetting('line1Color', e.target.value)}
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm font-monospace"
                      value={barSettings.line1Color || '#1e293b'}
                      onChange={(e) => handleUpdateSetting('line1Color', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Columna Línea 2: Bajada / Descripción */}
            <div className="col-12 col-md-4">
              <div className="p-3 rounded-4 border bg-light bg-opacity-75 h-100">
                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-secondary text-white rounded-pill px-2 py-1" style={{ fontSize: '0.7rem' }}>
                      Línea 2
                    </span>
                    <strong className="small text-dark">Bajada / Descripción</strong>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    <i className="bi bi-fonts me-1 text-secondary"></i> Tipografía (Fuente Línea 2)
                  </label>
                  <select
                    className="form-select form-select-sm fw-semibold rounded-3"
                    value={barSettings.line2FontFamily || 'inherit'}
                    onChange={(e) => handleUpdateSetting('line2FontFamily', e.target.value)}
                  >
                    <option value="inherit">Heredar de la barra / sitio</option>
                    {AVAILABLE_FONTS.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    <i className="bi bi-arrows-angle-expand me-1 text-secondary"></i> Tamaño de Fuente (Línea 2)
                  </label>
                  <select
                    className="form-select form-select-sm fw-semibold rounded-3"
                    value={barSettings.line2FontSize || 'sm'}
                    onChange={(e) => handleUpdateSetting('line2FontSize', e.target.value)}
                  >
                    <option value="2xs">Extra Chico (11px / 0.68rem)</option>
                    <option value="xs">Muy Chico (12px / 0.75rem)</option>
                    <option value="sm">Chico (13.5px / 0.84rem)</option>
                    <option value="md">Mediano (15px / 0.95rem)</option>
                    <option value="lg">Grande (17.5px / 1.10rem)</option>
                    <option value="xl">Extra Grande (20px / 1.25rem)</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    Grosor (Font Weight)
                  </label>
                  <select
                    className="form-select form-select-sm fw-semibold rounded-3"
                    value={barSettings.line2FontWeight || 'normal'}
                    onChange={(e) => handleUpdateSetting('line2FontWeight', e.target.value)}
                  >
                    <option value="normal">Normal (400)</option>
                    <option value="medium">Medio (500)</option>
                    <option value="semibold">Semi-Negrita (600)</option>
                    <option value="bold">Negrita (700)</option>
                  </select>
                </div>

                <div>
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    Color del Texto
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="color"
                      className="form-control form-control-color border rounded-3 p-1 cursor-pointer"
                      style={{ width: '36px', height: '30px' }}
                      value={barSettings.line2Color || '#64748b'}
                      onChange={(e) => handleUpdateSetting('line2Color', e.target.value)}
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm font-monospace"
                      value={barSettings.line2Color || '#64748b'}
                      onChange={(e) => handleUpdateSetting('line2Color', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Columna Línea 3: Línea Adicional / Detalle */}
            <div className="col-12 col-md-4">
              <div className="p-3 rounded-4 border bg-success bg-opacity-10 h-100 border-success-subtle">
                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom border-success-subtle">
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-success text-white rounded-pill px-2 py-1" style={{ fontSize: '0.7rem' }}>
                      Línea 3
                    </span>
                    <strong className="small text-dark">Línea Adicional / Detalle</strong>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    <i className="bi bi-fonts me-1 text-success"></i> Tipografía (Fuente Línea 3)
                  </label>
                  <select
                    className="form-select form-select-sm fw-semibold rounded-3"
                    value={barSettings.line3FontFamily || 'inherit'}
                    onChange={(e) => handleUpdateSetting('line3FontFamily', e.target.value)}
                  >
                    <option value="inherit">Heredar de la barra / sitio</option>
                    {AVAILABLE_FONTS.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    <i className="bi bi-arrows-angle-expand me-1 text-success"></i> Tamaño de Fuente (Línea 3)
                  </label>
                  <select
                    className="form-select form-select-sm fw-semibold rounded-3"
                    value={barSettings.line3FontSize || 'xs'}
                    onChange={(e) => handleUpdateSetting('line3FontSize', e.target.value)}
                  >
                    <option value="2xs">Extra Chico (11px / 0.68rem)</option>
                    <option value="xs">Chico (12px / 0.75rem)</option>
                    <option value="sm">Mediano (13.5px / 0.84rem)</option>
                    <option value="md">Grande (15px / 0.95rem)</option>
                    <option value="lg">Extra Grande (17.5px / 1.10rem)</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    Grosor (Font Weight)
                  </label>
                  <select
                    className="form-select form-select-sm fw-semibold rounded-3"
                    value={barSettings.line3FontWeight || 'medium'}
                    onChange={(e) => handleUpdateSetting('line3FontWeight', e.target.value)}
                  >
                    <option value="normal">Normal (400)</option>
                    <option value="medium">Medio (500)</option>
                    <option value="semibold">Semi-Negrita (600)</option>
                    <option value="bold">Negrita (700)</option>
                  </select>
                </div>

                <div>
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.74rem' }}>
                    Color del Texto
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="color"
                      className="form-control form-control-color border rounded-3 p-1 cursor-pointer"
                      style={{ width: '36px', height: '30px' }}
                      value={barSettings.line3Color || '#0d6efd'}
                      onChange={(e) => handleUpdateSetting('line3Color', e.target.value)}
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm font-monospace"
                      value={barSettings.line3Color || '#0d6efd'}
                      onChange={(e) => handleUpdateSetting('line3Color', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Vista Previa en Tiempo Real de TODOS los Pilares */}
          {items.length > 0 && (
            <div className="mt-3 pt-3 border-top">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small fw-bold text-muted">
                  <i className="bi bi-eye-fill me-1 text-primary"></i>
                  Vista Previa en Vivo de la Barra Completa ({items.length} {items.length === 1 ? 'Pilar' : 'Pilares'}):
                </span>
                <span className="badge bg-light text-muted border" style={{ fontSize: '0.7rem' }}>
                  Fondo, colores y 3 líneas en tiempo real
                </span>
              </div>
              <div
                className="p-3 rounded-4 border"
                style={{
                  backgroundColor: barSettings.backgroundColor || '#f8fafc',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <div className="row g-3">
                  {items.map((pItem, pIdx) => (
                    <div
                      key={pItem.id || pIdx}
                      className={
                        items.length === 1
                          ? 'col-12'
                          : items.length === 2
                          ? 'col-12 col-sm-6'
                          : items.length === 3
                          ? 'col-12 col-md-4'
                          : 'col-12 col-sm-6 col-xl-3'
                      }
                    >
                      <div
                        className="d-flex align-items-center gap-3 p-3 rounded-4 border shadow-2xs h-100"
                        style={{
                          backgroundColor: pItem.bgColor || '#ffffff',
                          fontFamily: getFontFamilyCss(barSettings.fontFamily) || 'inherit',
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: '44px',
                            height: '44px',
                            fontSize: '1.3rem',
                            backgroundColor: pItem.iconBg || 'rgba(13, 110, 253, 0.12)',
                            color: pItem.iconColor || '#0d6efd'
                          }}
                        >
                          <i className={`bi ${pItem.icon || 'bi-check-circle'}`}></i>
                        </div>
                        <div className="flex-grow-1">
                          <h6
                            className="mb-1"
                            style={{
                              fontFamily: getFontFamilyCss(barSettings.line1FontFamily || barSettings.fontFamily) || 'inherit',
                              color: barSettings.line1Color || '#1e293b',
                              fontSize: resolvePilarFontSizeCss(barSettings.line1FontSize, '0.95rem'),
                              fontWeight: resolvePilarFontWeightCss(barSettings.line1FontWeight, 700),
                              lineHeight: 1.25
                            }}
                          >
                            {pItem.title || `Pilar #${pIdx + 1}`}
                          </h6>
                          {pItem.description && (
                            <p
                              className="mb-0 lh-sm"
                              style={{
                                fontFamily: getFontFamilyCss(barSettings.line2FontFamily || barSettings.fontFamily) || 'inherit',
                                color: barSettings.line2Color || '#64748b',
                                fontSize: resolvePilarFontSizeCss(barSettings.line2FontSize, '0.84rem'),
                                fontWeight: resolvePilarFontWeightCss(barSettings.line2FontWeight, 400)
                              }}
                            >
                              {pItem.description}
                            </p>
                          )}
                          {pItem.extraText && (
                            <p
                              className="mb-0 lh-sm mt-1"
                              style={{
                                fontFamily: getFontFamilyCss(barSettings.line3FontFamily || barSettings.fontFamily) || 'inherit',
                                color: barSettings.line3Color || '#0d6efd',
                                fontSize: resolvePilarFontSizeCss(barSettings.line3FontSize, '0.75rem'),
                                fontWeight: resolvePilarFontWeightCss(barSettings.line3FontWeight, 500)
                              }}
                            >
                              {pItem.extraText}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grid de Tarjetas de Pilares */}
      <div className="row g-3">
        {items.map((item, idx) => (
          <div key={item.id || idx} className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 border shadow-xs rounded-4 overflow-hidden bg-white">
              {/* Card Header con Reordenamiento y Borrado */}
              <div className="card-header bg-light bg-opacity-75 py-2 px-3 border-bottom d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center gap-2">
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm px-2 py-1"
                      onClick={() => handleMoveItem(idx, -1)}
                      disabled={idx === 0}
                      title="Mover hacia la izquierda / antes"
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm px-2 py-1"
                      onClick={() => handleMoveItem(idx, 1)}
                      disabled={idx === items.length - 1}
                      title="Mover hacia la derecha / después"
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                  <span className="badge bg-primary text-white fw-bold font-monospace px-2 py-1 rounded-pill" style={{ fontSize: '0.72rem' }}>
                    #{idx + 1}
                  </span>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: '28px', height: '28px', padding: 0 }}
                  onClick={() => handleDeleteItem(idx)}
                  title="Eliminar este pilar"
                >
                  <i className="bi bi-trash3" style={{ fontSize: '0.78rem' }}></i>
                </button>
              </div>

              {/* Card Body con Edición del Pilar */}
              <div className="card-body p-3 d-flex flex-column gap-3">
                {/* Previsualización del Icono y Selector */}
                <div>
                  <label className="form-label text-muted fw-bold mb-2" style={{ fontSize: '0.75rem' }}>
                    Icono Representativo
                  </label>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: '44px',
                        height: '44px',
                        fontSize: '1.3rem',
                        backgroundColor: 'var(--ecom-primary-light, rgba(13, 110, 253, 0.12))',
                        color: 'var(--ecom-primary, #0d6efd)'
                      }}
                      title="Previsualización del icono"
                    >
                      <i className={`bi ${item.icon || 'bi-check-circle'}`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace fw-semibold"
                        value={item.icon || ''}
                        onChange={(e) => handleUpdateItem(idx, 'icon', e.target.value)}
                        placeholder="Ej. bi-truck"
                        style={{ fontSize: '0.8rem' }}
                      />
                    </div>
                  </div>

                  {/* Iconos Rápidos / Sugeridos */}
                  <div className="p-2 bg-light rounded-3 border" style={{ maxHeight: '92px', overflowY: 'auto' }}>
                    <div className="d-flex flex-wrap gap-1">
                      {POPULAR_BENEFIT_ICONS.map(pop => {
                        const isSelected = item.icon === pop.icon;
                        return (
                          <button
                            key={pop.icon}
                            type="button"
                            className={`btn btn-xs rounded-pill d-inline-flex align-items-center gap-1 ${
                              isSelected ? 'btn-primary text-white fw-bold' : 'btn-outline-secondary bg-white'
                            }`}
                            style={{ fontSize: '0.68rem', padding: '2px 7px' }}
                            onClick={() => handleUpdateItem(idx, 'icon', pop.icon)}
                            title={pop.label}
                          >
                            <i className={`bi ${pop.icon}`}></i>
                            <span>{pop.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Título del Pilar */}
                <div>
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem' }}>
                    Título Principal
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm fw-bold rounded-3"
                    value={item.title || ''}
                    onChange={(e) => handleUpdateItem(idx, 'title', e.target.value)}
                    placeholder="Ej. Envíos a Todo el País"
                    style={{ fontSize: '0.86rem' }}
                  />
                </div>

                {/* Subtítulo / Bajada */}
                <div>
                  <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem' }}>
                    Bajada / Descripción
                  </label>
                  <textarea
                    className="form-control form-control-sm rounded-3"
                    rows="2"
                    value={item.description || ''}
                    onChange={(e) => handleUpdateItem(idx, 'description', e.target.value)}
                    placeholder="Ej. Logística rápida y segura a todas las provincias..."
                    style={{ fontSize: '0.8rem' }}
                  ></textarea>
                </div>

                {/* Línea 3: Texto Adicional / Detalle */}
                <div>
                  <label className="form-label text-muted fw-bold mb-1 d-flex align-items-center justify-content-between" style={{ fontSize: '0.75rem' }}>
                    <span>
                      <i className="bi bi-fonts me-1 text-primary"></i> Línea 3 (Detalle / Texto Adicional)
                    </span>
                    <span className="badge bg-light text-muted border fw-normal" style={{ fontSize: '0.68rem' }}>Nueva</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm rounded-3"
                    value={item.extraText || ''}
                    onChange={(e) => handleUpdateItem(idx, 'extraText', e.target.value)}
                    placeholder="Ej. Envíos sin mínimo / Factura A y B..."
                    style={{ fontSize: '0.8rem' }}
                  />
                </div>

                {/* Color de Fondo de este Pilar */}
                <div>
                  <label className="form-label text-muted fw-bold mb-1 d-flex align-items-center justify-content-between" style={{ fontSize: '0.75rem' }}>
                    <span>
                      <i className="bi bi-paint-bucket me-2 text-primary"></i>Color de Fondo del Pilar #{idx + 1}
                    </span>
                    {item.bgColor && item.bgColor !== '#ffffff' && (
                      <button
                        type="button"
                        className="btn btn-link btn-xs p-0 text-decoration-none text-secondary fw-semibold"
                        style={{ fontSize: '0.7rem' }}
                        onClick={() => handleUpdateItem(idx, 'bgColor', '#ffffff')}
                        title="Volver a fondo blanco"
                      >
                        Restaurar blanco
                      </button>
                    )}
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="color"
                      className="form-control form-control-color border rounded-3 p-1 cursor-pointer flex-shrink-0 bg-white"
                      style={{ width: '40px', height: '32px' }}
                      value={item.bgColor || '#ffffff'}
                      onChange={(e) => handleUpdateItem(idx, 'bgColor', e.target.value)}
                      title={`Seleccionar color de fondo del Pilar #${idx + 1}`}
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm font-monospace rounded-3"
                      value={item.bgColor || '#ffffff'}
                      onChange={(e) => handleUpdateItem(idx, 'bgColor', e.target.value)}
                      placeholder="#ffffff"
                      style={{ fontSize: '0.8rem', height: '32px' }}
                    />
                  </div>
                </div>

                {/* Enlace Opcional de Redirección */}
                <div className="mt-auto pt-2 border-top">
                  <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: '0.72rem' }}>
                    <i className="bi bi-link-45deg me-1"></i> Enlace Opcional al hacer Clic
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm rounded-3 font-monospace"
                    value={item.link || ''}
                    onChange={(e) => handleUpdateItem(idx, 'link', e.target.value)}
                    placeholder="/tienda, /contacto o https://..."
                    style={{ fontSize: '0.75rem' }}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BenefitsAdminPanel;
